import React, { useCallback, useMemo, memo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Grid,
  CustomIconButton,
  MenuItem,
  TextField,
} from "../../../../components";
import { DeleteForever, DragIndicator } from "../../../../icons";
import {
  FILE_TYPE,
  CUSTOMER_DATA_TYPE,
  DTMF_DATA_TYPE,
  DIRECTION_DATA_TYPE,
  TTS_DATA_TYPE,
} from "../constants";
import VoiceFileInput from "./VoiceFileInput";
import VoiceCustomerDataInput from "./VoiceCustomerDataInput";
import VoiceDTMFInput from "./VoiceDTMF";
import { ErrorWrapper } from "./VoiceStepButtons";
import VoiceTTSInput from "./VoiceTTSInput";
import { omit, pickBy } from "lodash";
import {
  invalidVoiceFileTypeErrorValue,
  voiceFileUploadDefaultLabel,
  getStepErrorPrefix,
  getStepErrorVariableName,
  getStepErrorVariableValue,
  getStepErrorPath,
} from "../utils";

const getOrderRecipientHeaders = (recipientHeaders) =>
  Object.keys(recipientHeaders).sort();

function VoiceItems({
  voiceItems,
  errors,
  readOnly,
  onDeleteVoiceItem,
  onUploadVoiceFile,
  onRejectVoiceFile,
  onSetCustomerData,
  setVoiceCustomerData,
  onSetVoiceItems,
  onSetDTMFData,
  onSetDirectionData,
  onDTMFFilePathChange,
  onSaveAsDraftInitial,
  activeTab,
  onSetTTSData,
  testSection,
  selectedTtsVoice,
  templateType = "voiceTemplate",
}) {
  const { t } = useTranslation();

  const recipientHeaders = useSelector(
    (state) => state.eonsConfig.vendorConfig.recipientHeaders
  );

  const orderedRecipientHeaders = useMemo(
    () => getOrderRecipientHeaders(recipientHeaders),
    [recipientHeaders]
  );

  const readOnlyProps = useMemo(() => ({ readOnly }), [readOnly]);

  const onDragEnd = useCallback(
    (result) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }

      const sourceIdx = result.source.index;
      const destIdx = result.destination.index;

      let newVoiceItems = [...voiceItems];
      const [removedItem] = newVoiceItems.splice(sourceIdx, 1);
      newVoiceItems.splice(destIdx, 0, removedItem);

      const sourceErrorPrefix = getStepErrorPrefix(
        sourceIdx,
        activeTab,
        templateType
      );
      const destErrorPrefix = getStepErrorPrefix(
        destIdx,
        activeTab,
        templateType
      );
      const sourceErrors = pickBy(errors, (_, key) =>
        key.startsWith(sourceErrorPrefix)
      );

      const destErrors = pickBy(errors, (_, key) =>
        key.startsWith(destErrorPrefix)
      );

      const cleanedErrors = omit(
        errors,
        Object.keys(sourceErrors).concat(Object.keys(destErrors))
      );

      // Swap the prefixes
      const remappedSourceErrors = Object.fromEntries(
        Object.entries(sourceErrors).map(([key, value]) => [
          key.replace(sourceErrorPrefix, destErrorPrefix),
          value,
        ])
      );

      const remappedDestErrors = Object.fromEntries(
        Object.entries(destErrors).map(([key, value]) => [
          key.replace(destErrorPrefix, sourceErrorPrefix),
          value,
        ])
      );

      const updatedErrors = {
        ...cleanedErrors,
        ...remappedSourceErrors,
        ...remappedDestErrors,
      };
      // rearrange order of item idx
      newVoiceItems = newVoiceItems.map((e, index) => ({
        ...e,
        name: `${index}`,
      }));

      onSetVoiceItems(newVoiceItems, updatedErrors);
    },
    [voiceItems, errors, onSetVoiceItems, activeTab, templateType]
  );
  const getErrorPathValue = (stepIdx, activeTab, templateType) =>
    errors[getStepErrorPath(stepIdx, activeTab, templateType)];
  const getErrorVariableNameValue = (stepIdx, activeTab, templateType) =>
    errors[getStepErrorVariableName(stepIdx, activeTab, templateType)];
  const getErrorVariableValueValue = (stepIdx, activeTab, templateType) =>
    errors[getStepErrorVariableValue(stepIdx, activeTab, templateType)];

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="voiceItems">
        {(provided) => (
          <Grid
            container
            direction="column"
            spacing={2}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {voiceItems.map((voiceItem, idx) => (
              <Draggable
                key={voiceItem._id}
                draggableId={voiceItem?._id ?? voiceItem.name}
                index={idx}
              >
                {(provided) => (
                  <Grid
                    item
                    key={idx}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                  >
                    <Grid
                      container
                      spacing={2}
                      wrap="nowrap"
                      alignItems="flex-start"
                    >
                      <HandleBar
                        readOnly={readOnly}
                        {...provided.dragHandleProps}
                      />
                      <Grid item>{`${idx}`}</Grid>
                      <Grid item xs>
                        {(() => {
                          const { stepType } = voiceItem;
                          switch (stepType) {
                            case FILE_TYPE:
                              return (
                                <VoiceFileInput
                                  dataTestId={`Upload-audio-file-${testSection}-${idx}`}
                                  idx={idx}
                                  readOnly={readOnly}
                                  error={Boolean(
                                    getErrorPathValue(
                                      idx,
                                      activeTab,
                                      templateType
                                    )
                                  )}
                                  onUpload={onUploadVoiceFile}
                                  onReject={onRejectVoiceFile}
                                  filePath={voiceItem.path}
                                  label={
                                    getErrorPathValue(
                                      idx,
                                      activeTab,
                                      templateType
                                    )
                                      ? t(invalidVoiceFileTypeErrorValue)
                                      : truncateLongString(
                                          parseFileName(voiceItem.path)
                                        ) || t(voiceFileUploadDefaultLabel)
                                  }
                                />
                              );
                            case CUSTOMER_DATA_TYPE:
                              return (
                                <VoiceCustomerDataInput
                                  idx={idx}
                                  readOnly={readOnly}
                                  options={orderedRecipientHeaders.map(
                                    (header) => ({ title: header })
                                  )}
                                  label={t("Add Customer Data")}
                                  value={
                                    voiceItem.variableName
                                      ? voiceItem.variableName
                                      : ""
                                  }
                                  onChange={onSetCustomerData}
                                  readOnlyProps={readOnlyProps}
                                  error={Boolean(
                                    getErrorVariableNameValue(
                                      idx,
                                      activeTab,
                                      templateType
                                    )
                                  )}
                                  helperText={getErrorVariableNameValue(
                                    idx,
                                    activeTab,
                                    templateType
                                  )}
                                />
                              );
                            case DTMF_DATA_TYPE:
                              return (
                                <ErrorWrapper
                                  error={
                                    Object.keys(errors).filter((e) =>
                                      e.startsWith(
                                        `${templateType}.freeswitch.dialPlan.${activeTab}.steps.${idx}`
                                      )
                                    ).length > 0
                                  }
                                >
                                  <VoiceDTMFInput
                                    idx={idx}
                                    gotoOptions={voiceItems.map(
                                      (_, idx) => `${idx}`
                                    )}
                                    readOnly={readOnly}
                                    value={voiceItem.keys}
                                    onChange={onSetDTMFData}
                                    onDTMFFilePathChange={onDTMFFilePathChange}
                                    customerData={orderedRecipientHeaders}
                                    onSaveAsDraftInitial={onSaveAsDraftInitial}
                                    path={voiceItem.path}
                                    activeTab={activeTab}
                                    errors={errors}
                                    templateType={templateType}
                                  ></VoiceDTMFInput>
                                </ErrorWrapper>
                              );
                            case DIRECTION_DATA_TYPE:
                              return (
                                <TextField
                                  disabled={readOnly}
                                  data-test-id={`master-template-voice-dtmf-goto-${idx}`}
                                  select
                                  label={t("Go to Step")}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={voiceItem.step ? voiceItem.step : " "}
                                  onChange={(ev) =>
                                    onSetDirectionData(idx, ev.target.value)
                                  }
                                  InputProps={readOnlyProps}
                                  error={null}
                                  helperText={null}
                                >
                                  {voiceItems.map((_, subIdx) => {
                                    if (idx != subIdx)
                                      return (
                                        <MenuItem key={subIdx} value={subIdx}>
                                          {subIdx}
                                        </MenuItem>
                                      );
                                    if (subIdx == 0 && voiceItems.length < 2)
                                      return (
                                        <MenuItem
                                          key={subIdx}
                                          value={subIdx}
                                          disabled
                                        >
                                          {"Add more steps to select"}
                                        </MenuItem>
                                      );
                                  })}
                                </TextField>
                              );
                            case TTS_DATA_TYPE:
                              return (
                                <VoiceTTSInput
                                  selectedTtsVoice={selectedTtsVoice}
                                  idx={idx}
                                  readOnly={readOnly}
                                  voiceItem={voiceItem}
                                  errors={errors}
                                  activeTab={activeTab}
                                  onSetTTSData={onSetTTSData}
                                  readOnlyProps={readOnlyProps}
                                  testSection={testSection}
                                />
                              );
                            default:
                              return "";
                          }
                        })()}
                      </Grid>
                      {voiceItem.stepType === CUSTOMER_DATA_TYPE && (
                        <Grid item xs>
                          <TextField
                            fullWidth
                            disabled={
                              voiceItem.variableName && !readOnly ? false : true
                            }
                            size="small"
                            data-test-id="voice-free-form-test"
                            label={`${
                              voiceItem.variableName
                                ? voiceItem.variableName
                                : "Input"
                            } free form`}
                            variant="outlined"
                            onChange={(ev) =>
                              setVoiceCustomerData(idx, ev.target.value)
                            }
                            error={Boolean(
                              getErrorVariableValueValue(
                                idx,
                                activeTab,
                                templateType
                              )
                            )}
                            helperText={getErrorVariableValueValue(
                              idx,
                              activeTab,
                              templateType
                            )}
                            value={
                              voiceItem.variableValue
                                ? voiceItem.variableValue
                                : ""
                            }
                          ></TextField>
                        </Grid>
                      )}
                      <DeleteButton
                        readOnly={readOnly}
                        onDelete={onDeleteVoiceItem}
                        idx={idx}
                        testSection={testSection}
                      />
                    </Grid>
                  </Grid>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Grid>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function DeleteButton({ readOnly, onDelete, idx, testSection }) {
  return (
    !readOnly && (
      <Grid item>
        <CustomIconButton
          data-test-id={`master-template-delete-${testSection}-item-button-${idx}`}
          size="small"
          color="error"
          onClick={() => onDelete(idx)}
        >
          <DeleteForever />
        </CustomIconButton>
      </Grid>
    )
  );
}

function HandleBar({ readOnly, ...props }) {
  return (
    !readOnly && (
      <Grid item>
        <CustomIconButton
          data-test-id="master-template-drag-voice-item-button"
          size="small"
          color="default"
          {...props}
        >
          <DragIndicator />
        </CustomIconButton>
      </Grid>
    )
  );
}

function truncateLongString(str) {
  if (!str) {
    return false;
  }
  const maxLength = 50;
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + "...";
  }
  return str;
}

function parseFileName(path) {
  if (!path) return false;

  const arr = path.split("/");
  return arr[arr.length - 1];
}

export default memo(VoiceItems);
